import { createApi } from "@reduxjs/toolkit/query/react";
import { apiBaseQuery } from "../utils/apiBaseQuery";
import { ApiEnum } from "../enums/ApiEnum";
import { ProductInterface } from "../interfaces/ProductInterface";
import { ProductDetailsDataInterface } from "../interfaces/ProductDetailsDataInterface";
import { CategoryInterface } from "../interfaces/CategoryInterface";
import { FilterListInterface } from "../interfaces/FilterListInterface";
import { BrandingInterface } from "../interfaces/BrandingInterface";
import {
    DigitalSalesApiBranding,
    DigitalSalesApiCategoryNode,
    DigitalSalesApiFilter,
    DigitalSalesApiPaginationMeta,
    DigitalSalesApiProductIndexResponse,
} from "../interfaces/DigitalSalesApiInterface";
import {
    mapDigitalSalesBranding,
    mapDigitalSalesCategoryNode,
    mapDigitalSalesFilter,
    mapDigitalSalesProductEntry,
} from "../utils/digitalSalesV2Adapters";

export interface GetProductsResult {
    products: (ProductInterface & ProductDetailsDataInterface)[];
    meta: DigitalSalesApiPaginationMeta;
}

export const digitalSalesApiSlice = createApi({
    reducerPath: "digitalSalesApiSlice",
    baseQuery: apiBaseQuery(),
    tagTypes: [],
    endpoints: (builder) => ({
        getProducts: builder.query<
            GetProductsResult,
            {
                page: number;
                productIds?: number[];
            }
        >({
            query: (params) => {
                const { page, productIds } = params;
                const productIdFilter =
                    productIds && productIds.length > 0
                        ? `&filter[product_id]=${productIds.join(",")}`
                        : "";

                return {
                    url: `${ApiEnum.DIGITAL_SALES}/product?page=${page}${productIdFilter}`,
                    method: "GET",
                };
            },
            transformResponse: (
                response: DigitalSalesApiProductIndexResponse,
            ): GetProductsResult => ({
                products: response.data.map(mapDigitalSalesProductEntry),
                meta: response.meta,
            }),
        }),
        getProductCategory: builder.query<
            CategoryInterface[],
            {
                product_ids: number[];
            }
        >({
            query: (params) => {
                const { product_ids } = params;
                return {
                    url: `${ApiEnum.DIGITAL_SALES}/product-category?product_ids=${product_ids}`,
                    method: "GET",
                };
            },
            transformResponse: (
                response: DigitalSalesApiCategoryNode[],
            ): CategoryInterface[] =>
                (response ?? []).map(mapDigitalSalesCategoryNode),
        }),
        getFilters: builder.query<FilterListInterface[], void>({
            query: () => {
                return {
                    url: `${ApiEnum.DIGITAL_SALES}/filter/options`,
                    method: "GET",
                };
            },
            transformResponse: (
                response: DigitalSalesApiFilter[] | "",
            ): FilterListInterface[] =>
                Array.isArray(response)
                    ? response.map(mapDigitalSalesFilter)
                    : [],
        }),
        getBranding: builder.query<
            BrandingInterface[],
            {
                type: string;
            }
        >({
            query: (params) => {
                const { type } = params;
                return {
                    url: `${ApiEnum.DIGITAL_SALES}/branding?type=${type}`,
                    method: "GET",
                };
            },
            transformResponse: (
                response: DigitalSalesApiBranding[],
            ): BrandingInterface[] =>
                (response ?? []).map(mapDigitalSalesBranding),
        }),
    }),
});

export const { useGetProductsQuery } = digitalSalesApiSlice;
